import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button, Card, Col, Row, Alert, Spinner, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import ReactApexChart from "react-apexcharts";
import { useAuth } from "../hooks/useAuth";
import axios from "axios";

import { API_BASE_URL } from "../services/api";

// ============================================
// API CLIENT
// ============================================
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// API ENDPOINTS
// ============================================
const secureStatsApi = {
  fetchCountries: async () => {
    try {
      const response = await apiClient.get('/countries');
      return response.data;
    } catch (error) {
      console.error('Erreur fetchCountries:', error);
      return [];
    }
  },
  fetchTotalMessages: async (countryCode = '') => {
    try {
      const queryParams = countryCode ? `?country_code=${countryCode}` : '';
      const response = await apiClient.get(`/total-messages${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  fetchMessagesStats: async (countryCode = '') => {
    try {
      const queryParams = countryCode ? `?country_code=${countryCode}` : '';
      const response = await apiClient.get(`/messages-stats${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  fetchMonthlyDelivery: async (countryCode = '') => {
    try {
      const queryParams = countryCode ? `?country_code=${countryCode}` : '';
      const response = await apiClient.get(`/monthly-delivery${queryParams}`);
      return response.data;
    } catch (error) {
      return [];
    }
  },
  fetchStatus: async (countryCode = '') => {
    try {
      const queryParams = countryCode ? `?country_code=${countryCode}` : '';
      const response = await apiClient.get(`/status${queryParams}`);
      return response.data;
    } catch (error) {
      return [];
    }
  },
  fetchMonthlyCosts: async (countryCode = '') => {
    try {
      const queryParams = countryCode ? `?country_code=${countryCode}` : '';
      const response = await apiClient.get(`/monthly-costs${queryParams}`);
      return response.data;
    } catch (error) {
      return [];
    }
  },
  fetchOperatorPerformance: async (countryCode = '') => {
    try {
      const queryParams = countryCode ? `?country_code=${countryCode}` : '';
      const response = await apiClient.get(`/operator-performance${queryParams}`);
      return response.data;
    } catch (error) {
      return [];
    }
  }
};

// ============================================
// UTILITAIRES
// ============================================
const formatMonthYear = (dateValue) => {
  if (!dateValue && dateValue !== 0) return 'Date inconnue';
  try {
    const mois = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const dateString = dateValue.toString();

    if (/^\d{6}$/.test(dateString)) {
      const year = dateString.substring(0, 4);
      const monthIndex = parseInt(dateString.substring(4, 6)) - 1;
      if (monthIndex >= 0 && monthIndex < 12) return `${mois[monthIndex]} ${year}`;
    }
    if (/^\d{4}-\d{2}(-\d{2})?$/.test(dateString)) {
      const [year, month] = dateString.split('-');
      const monthIndex = parseInt(month) - 1;
      if (monthIndex >= 0 && monthIndex < 12) return `${mois[monthIndex]} ${year}`;
    }
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) return `${mois[date.getMonth()]} ${date.getFullYear()}`;
    return dateString;
  } catch {
    return dateValue?.toString() || 'Date';
  }
};

const parseNumberSafely = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const parsed = parseInt(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

const parseFloatSafely = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

// ============================================
// OPTIONS DES GRAPHIQUES (Premium)
// ============================================
const chartBaseFont = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const stackedBarOptions = {
  chart: {
    type: 'bar',
    toolbar: { show: false },
    height: 340,
    stacked: true,
    fontFamily: chartBaseFont.fontFamily,
    foreColor: '#64748b',
  },
  colors: ['#3b82f6', '#ef4444'],
  plotOptions: {
    bar: {
      borderRadius: 6,
      columnWidth: '55%',
      borderRadiusApplication: 'end',
      borderRadiusWhenStacked: 'all',
    }
  },
  grid: {
    borderColor: '#f1f5f9',
    strokeDashArray: 4,
    padding: { top: -10, left: 10, right: 10 }
  },
  xaxis: {
    categories: [],
    labels: {
      style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 500 },
      rotate: -45,
      trim: true,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: {
      formatter: (val) => Math.round(val).toLocaleString('fr-FR'),
      style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 500 }
    },
    min: 0,
  },
  tooltip: {
    theme: 'light',
    y: { formatter: (val) => Math.round(val).toLocaleString('fr-FR') + " SMS" }
  },
  legend: {
    position: 'top',
    horizontalAlign: 'right',
    offsetY: -5,
    fontSize: '12px',
    fontWeight: 600,
    markers: { radius: 4 },
    itemMargin: { horizontal: 12 }
  },
  fill: { opacity: 1 },
  dataLabels: { enabled: false },
};

const costOptions = {
  chart: {
    type: 'area',
    toolbar: { show: false },
    height: 340,
    fontFamily: chartBaseFont.fontFamily,
    foreColor: '#64748b',
  },
  colors: ['#10b981'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [0, 95, 100],
    }
  },
  stroke: {
    width: 3,
    curve: 'smooth',
    colors: ['#10b981'],
  },
  grid: {
    borderColor: '#f1f5f9',
    strokeDashArray: 4,
    padding: { top: -10, left: 10, right: 10 }
  },
  xaxis: {
    categories: [],
    labels: {
      style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 500 },
      rotate: -45,
      trim: true,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: {
      formatter: (val) => Math.round(val).toLocaleString('fr-FR'),
      style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 500 }
    },
  },
  tooltip: {
    theme: 'light',
    y: { formatter: (val) => Math.round(val).toLocaleString('fr-FR') + " Fcfa" }
  },
  markers: {
    size: 0,
    hover: { size: 6 },
  },
};

const statusOptions = {
  chart: {
    type: 'donut',
    height: 340,
    fontFamily: chartBaseFont.fontFamily,
    foreColor: '#64748b',
  },
  colors: ['#10b981', '#ef4444', '#f59e0b'],
  labels: ['Livrés', 'Échecs', 'En attente'],
  legend: {
    position: 'bottom',
    fontSize: '12px',
    fontWeight: 600,
    markers: { radius: 4 },
    itemMargin: { horizontal: 10 },
  },
  dataLabels: {
    enabled: true,
    formatter: (val) => parseFloat(val).toFixed(1) + "%",
    style: { fontSize: '11px', fontWeight: 700 },
    dropShadow: { enabled: false },
  },
  plotOptions: {
    pie: {
      donut: {
        size: '68%',
        labels: {
          show: true,
          name: { fontSize: '12px', color: '#64748b', fontWeight: 500 },
          value: {
            fontSize: '22px',
            fontWeight: 700,
            color: '#0f172a',
            formatter: (val) => parseFloat(val).toFixed(1) + "%"
          },
          total: {
            show: true,
            label: 'Taux de Livraison',
            fontSize: '12px',
            color: '#64748b',
            fontWeight: 500,
            formatter: (w) => {
              const series = w.globals.seriesTotals;
              const total = series.reduce((a, b) => a + b, 0);
              const delivered = series[0] || 0;
              const rate = total > 0 ? (delivered / total) * 100 : 0;
              return rate.toFixed(1) + "%";
            }
          }
        }
      }
    }
  },
  stroke: {
    width: 4,
    colors: ['#ffffff'],
  },
  tooltip: {
    theme: 'light',
    y: { formatter: (val) => parseFloat(val).toFixed(1) + "%" }
  }
};

// ============================================
// COMPOSANT
// ============================================
export default function SMSStatistics() {
  const { isAuthenticated, user, logout, loading: authLoading } = useAuth();

  // ✅ Ref pour empêcher le double fetch (StrictMode / re-render)
  const initDoneRef = useRef(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      window.location.href = '/login';
    }
  }, [isAuthenticated, authLoading]);

  const [smsStats, setSmsStats] = useState({
    totalSent: 0,
    totalDelivered: 0,
    deliveryRate: 0,
    totalCost: 0,
    todaySent: 0,
    monthlySent: 0,
    delivery_rate_today: 0,
    total_today: 0
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [costData, setCostData] = useState([]);
  const [operatorData, setOperatorData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');

  // ✅ Stable : aucune dépendance
  const fetchCountries = useCallback(async () => {
    try {
      const data = await secureStatsApi.fetchCountries();
      setCountries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur pays:', err);
      setCountries([]);
    }
  }, []);

  // ✅ Stable : dépend uniquement de isAuthenticated
  const fetchAllData = useCallback(async (countryCode = '') => {
    if (!isAuthenticated) {
      setError("Vous devez être connecté pour voir les statistiques");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const safeCountryCode = typeof countryCode === 'string' ? countryCode.substring(0, 10) : '';

      const [
        totalMessagesData,
        messagesStatsData,
        monthlyDeliveryData,
        statusDataResponse,
        monthlyCostsData,
        operatorPerformanceData
      ] = await Promise.all([
        secureStatsApi.fetchTotalMessages(safeCountryCode),
        secureStatsApi.fetchMessagesStats(safeCountryCode),
        secureStatsApi.fetchMonthlyDelivery(safeCountryCode),
        secureStatsApi.fetchStatus(safeCountryCode),
        secureStatsApi.fetchMonthlyCosts(safeCountryCode),
        secureStatsApi.fetchOperatorPerformance(safeCountryCode)
      ]);

      const extractValue = (data, field, defaultValue = 0) => {
        if (!data) return defaultValue;
        if (data.data && data.data[field] !== undefined) return data.data[field];
        if (data[field] !== undefined) return data[field];
        return defaultValue;
      };

      setSmsStats({
        totalSent: parseNumberSafely(extractValue(totalMessagesData, 'total_messages')),
        totalDelivered: 0,
        deliveryRate: parseFloatSafely(extractValue(totalMessagesData, 'delivery_rate')),
        totalCost: parseFloatSafely(extractValue(totalMessagesData, 'total_cost')),
        todaySent: parseNumberSafely(extractValue(totalMessagesData, 'total_today')),
        monthlySent: parseNumberSafely(messagesStatsData?.data?.m_message_count || messagesStatsData?.m_message_count),
        delivery_rate_today: parseFloatSafely(extractValue(totalMessagesData, 'delivery_rate_today')),
        total_today: parseNumberSafely(extractValue(totalMessagesData, 'total_today'))
      });

      const extractArray = (data) => {
        if (Array.isArray(data)) return data;
        if (data?.data && Array.isArray(data.data)) return data.data;
        return [];
      };

      setMonthlyData(extractArray(monthlyDeliveryData).slice(0, 12));
      setStatusData(extractArray(statusDataResponse));
      setCostData(extractArray(monthlyCostsData).slice(0, 12));
      setOperatorData(extractArray(operatorPerformanceData).slice(0, 10));

    } catch (err) {
      console.error('Erreur données:', err);
      let errorMessage = "Erreur lors du chargement des statistiques";
      if (err.response?.status === 401) {
        errorMessage = "Session expirée. Veuillez vous reconnecter.";
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      } else if (err.message) {
        errorMessage = `Erreur: ${err.message.substring(0, 200)}`;
      }
      setError(errorMessage);
      setSmsStats({
        totalSent: 0, totalDelivered: 0, deliveryRate: 0, totalCost: 0,
        todaySent: 0, monthlySent: 0, delivery_rate_today: 0, total_today: 0
      });
      setMonthlyData([]);
      setCostData([]);
      setStatusData([]);
      setOperatorData([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    if (typeof countryCode === 'string' && countryCode.length <= 10) {
      setSelectedCountry(countryCode);
      fetchAllData(countryCode);
    }
  };

  // ✅ Protection contre les appels multiples
  useEffect(() => {
    if (!isAuthenticated || initDoneRef.current) return;
    initDoneRef.current = true;

    const init = async () => {
      await fetchCountries();
      fetchAllData('');
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ============================================
  // PRÉPARATION DES DONNÉES
  // ============================================
  const prepareStackedBarData = () => {
    if (!Array.isArray(monthlyData) || monthlyData.length === 0) {
      return {
        series: [
          { name: 'Livrés', data: [] },
          { name: 'Non livrés', data: [] }
        ],
        options: { ...stackedBarOptions, xaxis: { ...stackedBarOptions.xaxis, categories: [] } }
      };
    }
    const sorted = [...monthlyData]
      .sort((a, b) => (a.mois_an?.toString() || '').localeCompare(b.mois_an?.toString() || ''))
      .slice(-12);
    return {
      series: [
        { name: 'Livrés', data: sorted.map(i => Math.round(parseNumberSafely(i.delivered))) },
        { name: 'Non livrés', data: sorted.map(i => Math.round(parseNumberSafely(i.undelivered))) }
      ],
      options: {
        ...stackedBarOptions,
        xaxis: { ...stackedBarOptions.xaxis, categories: sorted.map(i => formatMonthYear(i.mois_an)) }
      }
    };
  };

  const prepareCostData = () => {
    if (!Array.isArray(costData) || costData.length === 0) {
      return {
        series: [{ name: 'Coût mensuel', data: [] }],
        options: { ...costOptions, xaxis: { ...costOptions.xaxis, categories: [] } }
      };
    }
    const sorted = [...costData]
      .sort((a, b) => (a.mois_an?.toString() || '').localeCompare(b.mois_an?.toString() || ''))
      .slice(-12);
    return {
      series: [{ name: 'Coût mensuel', data: sorted.map(i => Math.round(parseFloatSafely(i.total_cost))) }],
      options: {
        ...costOptions,
        xaxis: { ...costOptions.xaxis, categories: sorted.map(i => formatMonthYear(i.mois_an)) }
      }
    };
  };

  const prepareStatusData = () => {
    if (!Array.isArray(statusData) || statusData.length === 0) {
      return { series: [0, 0, 0], options: statusOptions };
    }
    let delivered = 0, failed = 0, pending = 0;
    statusData.forEach(item => {
      const total = parseNumberSafely(item.total);
      if (item.sms_status === "200" || item.sms_status === "SENT") delivered += total;
      else if (["400", "500", "403"].includes(item.sms_status)) failed += total;
      else if (item.sms_status === "201" || item.sms_status === null) pending += total;
    });
    const total = delivered + failed + pending;
    const pct = (n) => total > 0 ? parseFloat(((n / total) * 100).toFixed(1)) : 0;
    return {
      series: [pct(delivered), pct(failed), pct(pending)],
      options: statusOptions
    };
  };

  const prepareOperatorData = () => {
    if (!Array.isArray(operatorData) || operatorData.length === 0) return [];
    return operatorData.map(op => {
      const delivered = parseNumberSafely(op.delivered);
      const total = parseNumberSafely(op.total, 1);
      return {
        name: typeof op.provider_sms === 'string' ? op.provider_sms.substring(0, 50)
              : typeof op.operator === 'string' ? op.operator : 'Inconnu',
        deliveryRate: total > 0 ? (delivered / total) * 100 : 0,
        total: total
      };
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="sms-dash main main-app p-3 p-lg-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" variant="primary" />
          <span className="ms-3 text-muted">Vérification de l'authentification...</span>
        </div>
      </div>
    );
  }

  const stackedBarData = prepareStackedBarData();
  const costChartData = prepareCostData();
  const statusChartData = prepareStatusData();
  const operatorPerformanceData = prepareOperatorData();

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bonjour';
    if (h < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  return (
    <div className="sms-dash main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="sms-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="sms-header-icon">
              <i className="ri-bar-chart-box-line"></i>
            </div>
            <div>
              <h3 className="sms-header-title mb-1">
                {getGreeting()}, {user?.firstname || 'Utilisateur'}
              </h3>
              <p className="sms-header-subtitle mb-0">
                Voici un aperçu de vos statistiques SMS en temps réel
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <div className="sms-country-select">
              <i className="ri-earth-line"></i>
              <Form.Select
                value={selectedCountry}
                onChange={handleCountryChange}
                disabled={loading}
                className="sms-select"
              >
                <option value="">Tous les pays</option>
                {Array.isArray(countries) && countries.map((c) => (
                  <option key={c.id || c.country_code} value={c.country_code}>
                    {c.country_name || c.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <Button
              className="sms-btn-refresh"
              onClick={() => fetchAllData(selectedCountry)}
              disabled={loading}
            >
              <i className={`ri-refresh-line ${loading ? 'spinning' : ''}`}></i>
              <span>Actualiser</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== ERREUR ========== */}
      {error && (
        <div className="sms-alert sms-alert-danger mb-4">
          <div className="d-flex align-items-center gap-2">
            <i className="ri-error-warning-fill fs-5"></i>
            <div>
              <div className="fw-bold">Erreur de chargement</div>
              <div className="small opacity-75">{error}</div>
            </div>
          </div>
          <Button className="sms-btn-retry" size="sm" onClick={() => fetchAllData(selectedCountry)}>
            <i className="ri-refresh-line me-1"></i>
            Réessayer
          </Button>
        </div>
      )}

      {/* ========== STATS CARDS ========== */}
      <Row className="g-3 mb-4">
        {/* Total envoyés */}
        <Col sm={6} lg={3}>
          <div className="sms-stat-card sms-stat-blue">
            <div className="sms-stat-bg-icon">
              <i className="ri-send-plane-fill"></i>
            </div>
            <div className="sms-stat-header">
              <div className="sms-stat-icon-wrapper sms-stat-icon-blue">
                <i className="ri-send-plane-fill"></i>
              </div>
              <span className="sms-stat-trend sms-trend-up">
                <i className="ri-arrow-up-line"></i> Total
              </span>
            </div>
            <div className="sms-stat-body">
              <div className="sms-stat-label">Total envoyés</div>
              <div className="sms-stat-value">
                {loading ? <Spinner animation="border" size="sm" /> : smsStats.totalSent.toLocaleString('fr-FR')}
              </div>
              <div className="sms-stat-footer">
                <i className="ri-time-line"></i>
                Depuis le début
              </div>
            </div>
          </div>
        </Col>

        {/* Taux de livraison */}
        <Col sm={6} lg={3}>
          <div className="sms-stat-card sms-stat-green">
            <div className="sms-stat-bg-icon">
              <i className="ri-checkbox-circle-fill"></i>
            </div>
            <div className="sms-stat-header">
              <div className="sms-stat-icon-wrapper sms-stat-icon-green">
                <i className="ri-checkbox-circle-fill"></i>
              </div>
              <span className="sms-stat-trend sms-trend-up">
                <i className="ri-arrow-up-line"></i> Performance
              </span>
            </div>
            <div className="sms-stat-body">
              <div className="sms-stat-label">Taux de livraison</div>
              <div className="sms-stat-value">
                {loading ? <Spinner animation="border" size="sm" /> : `${smsStats.deliveryRate.toFixed(1)}%`}
              </div>
              <div className="sms-stat-progress">
                <div
                  className="sms-stat-progress-bar"
                  style={{
                    width: `${Math.min(smsStats.deliveryRate, 100)}%`,
                    background: 'linear-gradient(90deg, #10b981, #34d399)'
                  }}
                ></div>
              </div>
            </div>
          </div>
        </Col>

        {/* Coût total */}
        <Col sm={6} lg={3}>
          <div className="sms-stat-card sms-stat-amber">
            <div className="sms-stat-bg-icon">
              <i className="ri-money-euro-circle-fill"></i>
            </div>
            <div className="sms-stat-header">
              <div className="sms-stat-icon-wrapper sms-stat-icon-amber">
                <i className="ri-money-euro-circle-fill"></i>
              </div>
              <span className="sms-stat-trend sms-trend-neutral">
                <i className="ri-wallet-3-line"></i> Dépenses
              </span>
            </div>
            <div className="sms-stat-body">
              <div className="sms-stat-label">Coût total</div>
              <div className="sms-stat-value">
                {loading ? <Spinner animation="border" size="sm" /> : (
                  <>{smsStats.totalCost.toLocaleString('fr-FR')} <span className="sms-stat-unit">Fcfa</span></>
                )}
              </div>
              <div className="sms-stat-footer">
                <i className="ri-calculator-line"></i>
                Moyenne: {smsStats.totalSent > 0 ? (smsStats.totalCost / smsStats.totalSent).toFixed(0) : 0} Fcfa/SMS
              </div>
            </div>
          </div>
        </Col>

        {/* Aujourd'hui */}
        <Col sm={6} lg={3}>
          <div className="sms-stat-card sms-stat-violet">
            <div className="sms-stat-bg-icon">
              <i className="ri-calendar-event-fill"></i>
            </div>
            <div className="sms-stat-header">
              <div className="sms-stat-icon-wrapper sms-stat-icon-violet">
                <i className="ri-calendar-event-fill"></i>
              </div>
              <span className="sms-stat-trend sms-trend-up">
                <i className="ri-flashlight-fill"></i> Live
              </span>
            </div>
            <div className="sms-stat-body">
              <div className="sms-stat-label">Aujourd'hui</div>
              <div className="sms-stat-value">
                {loading ? <Spinner animation="border" size="sm" /> : smsStats.total_today.toLocaleString('fr-FR')}
              </div>
              <div className="sms-stat-footer">
                <i className="ri-check-double-line"></i>
                Taux: {smsStats.delivery_rate_today.toFixed(1)}%
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== CHARTS ========== */}
      {loading ? (
        <div className="sms-loading-container">
          <div className="sms-loading-spinner"></div>
          <div className="sms-loading-text">Chargement des statistiques...</div>
        </div>
      ) : (
        <Row className="g-3">
          {/* Volume par mois */}
          <Col xl={8}>
            <div className="sms-chart-card">
              <div className="sms-chart-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="sms-chart-icon sms-chart-icon-blue">
                    <i className="ri-bar-chart-2-fill"></i>
                  </div>
                  <div>
                    <h6 className="sms-chart-title">Volume des messages</h6>
                    <small className="sms-chart-subtitle">Livrés vs non livrés par mois</small>
                  </div>
                </div>
              </div>
              <div className="sms-chart-body">
                {stackedBarData.series[0].data.length > 0 ? (
                  <ReactApexChart
                    series={stackedBarData.series}
                    options={stackedBarData.options}
                    type="bar"
                    height={340}
                  />
                ) : (
                  <div className="sms-empty-state">
                    <i className="ri-bar-chart-line"></i>
                    <p>Aucune donnée disponible</p>
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* Statut */}
          <Col xl={4}>
            <div className="sms-chart-card">
              <div className="sms-chart-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="sms-chart-icon sms-chart-icon-green">
                    <i className="ri-pie-chart-2-fill"></i>
                  </div>
                  <div>
                    <h6 className="sms-chart-title">Statut des messages</h6>
                    <small className="sms-chart-subtitle">Répartition globale</small>
                  </div>
                </div>
              </div>
              <div className="sms-chart-body">
                {(statusChartData.series[0] > 0 || statusChartData.series[1] > 0 || statusChartData.series[2] > 0) ? (
                  <ReactApexChart
                    series={statusChartData.series}
                    options={statusChartData.options}
                    type="donut"
                    height={340}
                  />
                ) : (
                  <div className="sms-empty-state">
                    <i className="ri-pie-chart-line"></i>
                    <p>Aucune donnée de statut</p>
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* Coûts */}
          <Col xl={6}>
            <div className="sms-chart-card">
              <div className="sms-chart-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="sms-chart-icon sms-chart-icon-emerald">
                    <i className="ri-line-chart-fill"></i>
                  </div>
                  <div>
                    <h6 className="sms-chart-title">Évolution des coûts</h6>
                    <small className="sms-chart-subtitle">Coûts mensuels en Fcfa</small>
                  </div>
                </div>
              </div>
              <div className="sms-chart-body">
                {costChartData.series[0].data.length > 0 ? (
                  <ReactApexChart
                    series={costChartData.series}
                    options={costChartData.options}
                    type="area"
                    height={340}
                  />
                ) : (
                  <div className="sms-empty-state">
                    <i className="ri-line-chart-line"></i>
                    <p>Aucune donnée de coût</p>
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* Opérateurs */}
          <Col xl={6}>
            <div className="sms-chart-card">
              <div className="sms-chart-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="sms-chart-icon sms-chart-icon-violet">
                    <i className="ri-signal-tower-fill"></i>
                  </div>
                  <div>
                    <h6 className="sms-chart-title">Performance par opérateur</h6>
                    <small className="sms-chart-subtitle">Taux de livraison par réseau</small>
                  </div>
                </div>
              </div>
              <div className="sms-chart-body sms-operators-body">
                {operatorPerformanceData.length > 0 ? (
                  operatorPerformanceData.map((op, i) => {
                    const level = op.deliveryRate >= 95 ? 'high' : op.deliveryRate >= 90 ? 'mid' : 'low';
                    const levelConfig = {
                      high: { color: '#10b981', bg: '#d1fae5', icon: 'ri-arrow-up-line', label: 'Excellent' },
                      mid:  { color: '#f59e0b', bg: '#fef3c7', icon: 'ri-arrow-right-line', label: 'Moyen' },
                      low:  { color: '#ef4444', bg: '#fee2e2', icon: 'ri-arrow-down-line', label: 'Critique' },
                    }[level];

                    return (
                      <div key={i} className="sms-operator-item">
                        <div className="sms-operator-header">
                          <div className="d-flex align-items-center gap-2">
                            <div className="sms-operator-avatar" style={{ background: levelConfig.bg, color: levelConfig.color }}>
                              <i className="ri-signal-tower-line"></i>
                            </div>
                            <div>
                              <div className="sms-operator-name">{op.name}</div>
                              <div className="sms-operator-meta">
                                {op.total.toLocaleString('fr-FR')} messages
                              </div>
                            </div>
                          </div>
                          <div className="sms-operator-rate" style={{ color: levelConfig.color }}>
                            <span className="sms-operator-rate-value">{op.deliveryRate.toFixed(1)}%</span>
                            <i className={`${levelConfig.icon} sms-operator-rate-icon`}></i>
                          </div>
                        </div>
                        <div className="sms-operator-progress">
                          <div
                            className="sms-operator-progress-bar"
                            style={{
                              width: `${Math.min(op.deliveryRate, 100)}%`,
                              background: `linear-gradient(90deg, ${levelConfig.color}, ${levelConfig.color}dd)`
                            }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="sms-empty-state">
                    <i className="ri-wifi-off-line"></i>
                    <p>Aucune donnée d'opérateur</p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .sms-dash {
          background: #f8fafc;
          min-height: 100vh;
        }
        .sms-dash * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .sms-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sms-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .sms-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .sms-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }
        .sms-country-select {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .sms-country-select > i {
          position: absolute;
          left: 14px;
          color: #64748b;
          z-index: 5;
          pointer-events: none;
        }
        .sms-select {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 10px 18px 10px 40px;
          font-size: 0.875rem;
          font-weight: 500;
          background-color: white;
          min-width: 220px;
          transition: all 0.2s;
        }
        .sms-select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .sms-btn-refresh {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          transition: all 0.2s;
        }
        .sms-btn-refresh:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .sms-btn-refresh:disabled {
          opacity: 0.6;
        }

        /* ========== ALERT ========== */
        .sms-alert {
          border-radius: 12px;
          border: none;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .sms-alert-danger {
          background: #fef2f2;
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .sms-btn-retry {
          background: white;
          color: #991b1b;
          border: 1px solid #fecaca;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.8rem;
          padding: 6px 14px;
        }
        .sms-btn-retry:hover {
          background: #991b1b;
          color: white;
          border-color: #991b1b;
        }

        /* ========== STATS CARDS ========== */
        .sms-stat-card {
          position: relative;
          background: white;
          border-radius: 16px;
          padding: 22px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          transition: all 0.25s;
          height: 100%;
        }
        .sms-stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 32px -12px rgba(0,0,0,0.1);
          border-color: transparent;
        }
        .sms-stat-bg-icon {
          position: absolute;
          top: -20px;
          right: -20px;
          font-size: 110px;
          opacity: 0.04;
          line-height: 1;
          transition: opacity 0.25s;
        }
        .sms-stat-card:hover .sms-stat-bg-icon {
          opacity: 0.08;
        }
        .sms-stat-blue .sms-stat-bg-icon { color: #3b82f6; }
        .sms-stat-green .sms-stat-bg-icon { color: #10b981; }
        .sms-stat-amber .sms-stat-bg-icon { color: #f59e0b; }
        .sms-stat-violet .sms-stat-bg-icon { color: #8b5cf6; }

        .sms-stat-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 18px;
        }
        .sms-stat-icon-wrapper {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .sms-stat-icon-blue { background: #eff6ff; color: #3b82f6; }
        .sms-stat-icon-green { background: #d1fae5; color: #10b981; }
        .sms-stat-icon-amber { background: #fef3c7; color: #f59e0b; }
        .sms-stat-icon-violet { background: #ede9fe; color: #8b5cf6; }

        .sms-stat-trend {
          font-size: 0.7rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 20px;
          display: inline-flex;
          align-items: center;
          gap: 3px;
        }
        .sms-trend-up {
          background: #d1fae5;
          color: #059669;
        }
        .sms-trend-neutral {
          background: #f1f5f9;
          color: #64748b;
        }

        .sms-stat-body {
          position: relative;
          z-index: 1;
        }
        .sms-stat-label {
          font-size: 0.7rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 6px;
        }
        .sms-stat-value {
          font-size: 1.85rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.03em;
          line-height: 1.1;
          display: flex;
          align-items: center;
        }
        .sms-stat-unit {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          margin-left: 6px;
        }
        .sms-stat-footer {
          font-size: 0.72rem;
          color: #94a3b8;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .sms-stat-progress {
          height: 5px;
          background: #f1f5f9;
          border-radius: 10px;
          margin-top: 12px;
          overflow: hidden;
        }
        .sms-stat-progress-bar {
          height: 100%;
          border-radius: 10px;
          transition: width 0.8s ease;
        }

        /* ========== CHARTS ========== */
        .sms-chart-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
          height: 100%;
          transition: all 0.25s;
        }
        .sms-chart-card:hover {
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.08);
          border-color: #cbd5e1;
        }
        .sms-chart-header {
          padding: 20px 24px;
          border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
        }
        .sms-chart-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .sms-chart-icon-blue { background: #eff6ff; color: #3b82f6; }
        .sms-chart-icon-green { background: #d1fae5; color: #10b981; }
        .sms-chart-icon-emerald { background: #d1fae5; color: #059669; }
        .sms-chart-icon-violet { background: #ede9fe; color: #8b5cf6; }

        .sms-chart-title {
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          font-size: 0.95rem;
          letter-spacing: -0.01em;
        }
        .sms-chart-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .sms-chart-body {
          padding: 20px 24px;
        }
        .sms-operators-body {
          max-height: 400px;
          overflow-y: auto;
        }
        .sms-operators-body::-webkit-scrollbar {
          width: 6px;
        }
        .sms-operators-body::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .sms-operators-body::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }

        /* ========== OPERATORS ========== */
        .sms-operator-item {
          padding: 14px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .sms-operator-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        .sms-operator-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        .sms-operator-avatar {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
        }
        .sms-operator-name {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.875rem;
        }
        .sms-operator-meta {
          font-size: 0.72rem;
          color: #94a3b8;
        }
        .sms-operator-rate {
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 700;
        }
        .sms-operator-rate-value {
          font-size: 0.95rem;
          font-variant-numeric: tabular-nums;
        }
        .sms-operator-rate-icon {
          font-size: 1rem;
        }
        .sms-operator-progress {
          height: 6px;
          background: #f1f5f9;
          border-radius: 10px;
          overflow: hidden;
        }
        .sms-operator-progress-bar {
          height: 100%;
          border-radius: 10px;
          transition: width 0.8s ease;
        }

        /* ========== EMPTY STATE ========== */
        .sms-empty-state {
          padding: 60px 20px;
          text-align: center;
          color: #94a3b8;
        }
        .sms-empty-state i {
          font-size: 3rem;
          opacity: 0.5;
          display: block;
          margin-bottom: 12px;
        }
        .sms-empty-state p {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 500;
        }

        /* ========== LOADING ========== */
        .sms-loading-container {
          padding: 100px 20px;
          text-align: center;
        }
        .sms-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .sms-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }

        /* ========== ANIMATIONS ========== */
        .spinning {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .sms-header {
            padding: 20px;
          }
          .sms-header-title {
            font-size: 1.2rem;
          }
          .sms-stat-value {
            font-size: 1.5rem;
          }
          .sms-select {
            min-width: 100%;
          }
        }
      `}</style>
    </div>
  );
}